import SaveTwoToneIcon from "@mui/icons-material/SaveTwoTone";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { checkUserRights, type RightDefinition, type UserRightDraft } from "./userRightsValidation";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import CancelTwoToneIcon from "@mui/icons-material/CancelTwoTone";
import { AxiosResponse } from "axios";
import { AddCircleOutlineTwoTone, ArrowRight, DeleteTwoTone } from "@mui/icons-material";
import { User as BitumenUser, Right as BitumenRight, WithId } from "../types_bitumen";
import { User as TransUser, Right as TransRight } from "../types";
import useRights from "../api/useRights";
import BaseApiService from "../api/BaseApiService";
import { TopBar } from "../structure/TopBar";
import { validate } from "../api/validate";
import { MainContainer } from "../structure/MainContainer";
import ErrorIndicator from "../dialogs/ErrorIndicator";
import ManyToOneSelect from "./ManyToOneSelect";
import ManyToManySelect from "./ManyToManySelect";
import ErrorDialog from "../dialogs/ErrorDialog";

type UniversalUser = (BitumenUser | TransUser) & { firstName?: string };
type Right = BitumenRight | TransRight;


type EditUsersProps = {
  hasFirstName?: boolean;
  allowEmptyRights?: boolean;
  setPageTitle: (title: string) => void;
  renderRoleAssistant?: (props: { allRights: Record<string, RightDefinition>; onComplete: (roleId: number, draft: UserRightDraft[]) => void }) => ReactNode;
};

export default function EditUsers(props: EditUsersProps) {
  const rights = useRights();
  const { rawId } = useParams();
  if (!("USERADMIN" in rights)) return <Navigate replace to="/" />;
  return <UserEditor key={rawId ?? "new"} {...props} rights={rights} />;
}

function UserEditor({ setPageTitle, hasFirstName = false, allowEmptyRights = false, renderRoleAssistant, rights }: EditUsersProps & { rights: Record<string, string> }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { rawId } = useParams();


  const id = !rawId ? 0 : parseInt(rawId);

  const [name, setName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [editPassword, setEditPassword] = useState(true);
  const [pW, setPW] = useState("");
  const [userRights, setUserRights] = useState<UniversalUser["rights"]>([]);

  const [roleTemplateId, setRoleTemplateId] = useState<number | null>(null);
  const [replaceUserRights, setReplaceUserRights] = useState(false);

  const setField = (id: number, changes: Partial<UniversalUser["rights"][0]>) => {
    setRoleTemplateId(null);
    const nur = userRights.slice();
    nur[id] = { ...nur[id], ...changes };
    setUserRights(nur);
  };

  useEffect(() => setPageTitle(id > 0 ? "Benutzer bearbeiten" : "Neuer Benutzer"), [id, setPageTitle]);

  const [validationMessages, setValidationMessages] = useState<string[]>([]);

  const [shouldFetch, setShouldFetch] = useState(id > 0);
  useEffect(() => {
    setRoleTemplateId(null);
    setReplaceUserRights(false);
    setShouldFetch(id > 0);
  }, [id]);

  const query = useQuery<UniversalUser, AxiosResponse>({
    queryKey: ["getUser", id],
    queryFn: BaseApiService.getEntity("user", id),
    enabled: shouldFetch,
  });

  useEffect(() => {
    if (!shouldFetch || !query.isSuccess || !query.data) return;
    setShouldFetch(false);
    setName(query.data.name);
    setEditPassword(false);
    setPW(query.data.pW);
    setFirstName(query.data?.firstName ?? "");
    setEmail(query.data.email);
    setUserRights(query.data.rights);
  }, [query.isSuccess, query.data, shouldFetch]);

  const rightsQuery = useQuery<Right[], AxiosResponse>({
    queryKey: ["getRights"],
    queryFn: BaseApiService.getEntities("right"),
  });

  const allRights: { [name: string]: Right } = useMemo(() => {
    if (!rightsQuery.data || rightsQuery.data.length === 0) return {};
    const out: { [name: string]: Right } = {};
    for (const o of rightsQuery.data) {
      if (o.visibilityConstraint.length > 0 && !(o.visibilityConstraint in rights)) continue;
      out[o.name] = o;
    }
    return out;
  }, [rightsQuery.data, rights]);

  const mutation = useMutation<unknown, AxiosResponse, UniversalUser>({
    mutationFn: id > 0 ? BaseApiService.editEntity("user", id) : BaseApiService.createEntity("user"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getUsers"] });
      if (id > 0) queryClient.invalidateQueries({ queryKey: ["getUser", id] });
      navigate(-1 /*"/settings/users"*/);
    },
  });

  const valid = checkUserRights(userRights, allRights, allowEmptyRights);

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        bgcolor: "background.paper",
        overflow: "hidden",
      }}
    >
      <TopBar>
        <LoadingButton
          onClick={() =>
            validate({ name, email, rights: userRights, pW, ...(hasFirstName ? { firstName } : {}), ...(renderRoleAssistant && replaceUserRights ? { replaceUserRights, roleTemplateId } : {}) }, mutation.mutate, setValidationMessages)
          }
          loading={mutation.status === "pending"}
          variant="text"
          disabled={(!query.isError && shouldFetch) || query.isError || valid.length > 0}
          startIcon={<SaveTwoToneIcon />}
        >
          Speichern
        </LoadingButton>
        <Button onClick={() => navigate(-1 /*"/settings/users"*/)} variant="text" startIcon={<CancelTwoToneIcon />}>
          Abbrechen
        </Button>
      </TopBar>
      <MainContainer>
        {(!query.isError && shouldFetch) || rightsQuery.isLoading ? (
          <Box
            sx={{
              display: "flex",
              padding: 1,
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : query.isError || rightsQuery.isError ? (
          <ErrorIndicator />
        ) : (
          <Box sx={{ padding: 2 }}>
            {validationMessages.length ? (
              <Alert severity="error">
                <AlertTitle>Fehlende Daten</AlertTitle>
                {validationMessages.map((message, key) => (
                  <div key={key}>{message}</div>
                ))}
              </Alert>
            ) : null}
            {mutation.isError ? <Alert severity="error">{mutation.error?.data?.error ?? "Speichern fehlgeschlagen. Die Änderungen wurden nicht übernommen."}</Alert> : null}
            <TextField label="Name" fullWidth margin="dense" value={name} onChange={(e) => setName((e.target as HTMLInputElement).value)} />
            {hasFirstName ? (
              <TextField
                label="Vorname"
                fullWidth
                margin="dense"
                value={firstName}
                onChange={(e) => setFirstName((e.target as HTMLInputElement).value)}
              />
            ) : null}
            <TextField label="E-Mail" fullWidth margin="dense" value={email} onChange={(e) => setEmail((e.target as HTMLInputElement).value)} />
            <Box sx={{ display: "flex", columnGap: "10px", mt: "10px" }}>
              {id > 0 ? (
                <ToggleButtonGroup
                  value={editPassword}
                  exclusive
                  onChange={(_, x) => {
                    if (!x) setPW(query.data?.pW ?? "");
                    else setPW("");
                    setEditPassword(x);
                  }}
                >
                  <ToggleButton value={false}>Passwort&nbsp;behalten</ToggleButton>
                  <ToggleButton value={true}>Passwort&nbsp;setzen</ToggleButton>
                </ToggleButtonGroup>
              ) : null}
              {editPassword ? (
                <TextField
                  label="Passwort"
                  fullWidth
                  margin="none"
                  value={pW}
                  type="password"
                  onChange={(e) => setPW((e.target as HTMLInputElement).value)}
                />
              ) : null}
            </Box>
            {renderRoleAssistant?.({ allRights, onComplete: (roleId, draft) => {
              setUserRights(draft);
              setRoleTemplateId(roleId);
              setReplaceUserRights(true);
            } })}
            {replaceUserRights ? <Alert severity="info" sx={{ mt: 1 }}>Beim Speichern werden die bisherigen Rechte vollständig durch den aktuellen Entwurf ersetzt.</Alert> : null}
            <Typography variant="h6" sx={{ mt: "10px" }} gutterBottom>
              Berechtigungen
            </Typography>
            <Box sx={{ border: "1px solid #aaa", borderRadius: "5px" }}>
              <Box sx={{ p: "10px", borderBottom: "1px solid #aaa" }}>
                {userRights.map((u, i) =>
                  !u.deleted && (!u.right || u.right in allRights) ? (
                    <Box key={i} sx={{ display: "flex", columnGap: "10px", mb: "10px" }}>
                      <FormControl sx={{ minWidth: "25%" }}>
                        <InputLabel id={"label" + i}>Berechtigung</InputLabel>
                        <Select
                          labelId={"label" + i}
                          value={u.right ?? ""}
                          label="Berechtigung"
                          onChange={(e) => {
                            setField(i, {
                              right: e.target.value as string,
                              stringVal: null,
                              intVal: allRights[e.target.value].type === "Boolean" ? 1 : null,
                            });
                          }}
                        >
                          <MenuItem value="" disabled>Berechtigung auswählen</MenuItem>
                          {Object.values(allRights)
                            .sort((a, b) => a.ordering.localeCompare(b.ordering))
                            .map((x) => (
                              <MenuItem key={x.name} value={x.name}>
                                {x.description}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                      {!allRights[u.right] ? null : allRights[u.right].type === "ManyToOne" ? (
                        <ManyToOneSelect<{ name: string } & WithId>
                          sx={{ mt: 0 }}
                          key={allRights[u.right].entity}
                          entity={allRights[u.right].entity}
                          currentId={!u.stringVal ? 0 : parseInt(u.stringVal)}
                          setId={(val) => setField(i, { stringVal: "" + val })}
                          label={"Zugehörige Einheit"}
                        />
                      ) : allRights[u.right].type === "ManyToMany" ? (
                        <ManyToManySelect<{ name: string } & WithId>
                          sx={{ mt: 0, flex: 1 }}
                          key={allRights[u.right].entity}
                          entity={allRights[u.right].entity}
                          currentIds={!u.stringVal ? [] : u.stringVal.split(",").map((x) => parseInt(x))}
                          setIds={(val) => setField(i, { stringVal: val.join(",") })}
                          label={"Zugehörige Einheiten"}
                        />
                      ) : allRights[u.right].type === "Boolean" ? (
                        <Checkbox checked={u.intVal === 1} onChange={(e) => setField(i, { intVal: e.target.checked ? 1 : 0 })} />
                      ) : (
                        "Fehler"
                      )}
                      <IconButton sx={{ flexShrink: 1, alignSelf: "center" }} onClick={() => setField(i, { deleted: true })}>
                        <DeleteTwoTone />
                      </IconButton>
                    </Box>
                  ) : null,
                )}
                <Button
                  sx={{ mt: "4px" }}
                  onClick={() => {
                    setRoleTemplateId(null);
                    const nur = userRights.slice();
                    nur.push({
                      id: 0,
                      right: null,
                      intVal: null,
                      stringVal: null,
                    });
                    setUserRights(nur);
                  }}
                  variant="contained"
                  startIcon={<AddCircleOutlineTwoTone />}
                >
                  Berechtigung hinzufügen
                </Button>
              </Box>
              <Alert severity={valid.length > 0 ? "warning" : "success"}>
                <AlertTitle>{valid.length > 0 ? "Ungültig" : "Gültig"}</AlertTitle>
                {valid.map((x, i) => (
                  <Box sx={{ display: "flex", alignItems: "center" }} key={i}>
                    <ArrowRight />
                    {x}
                  </Box>
                ))}
              </Alert>
            </Box>
          </Box>
        )}
      </MainContainer>
      <ErrorDialog
        forceOpen={
          (query.isError && query.error.status === 401) ||
          (mutation.isError && mutation.error.status === 401) ||
          (rightsQuery.isError && rightsQuery.error.status === 401)
        }
      />
    </Box>
  );
}
